/**
 * InsufficientCreditsModal — opens when backend returns HTTP 402.
 *
 * Reads the structured `detail` from `ApiError` (which mirrors the backend
 * response: { code, message_tr, current_balance, required_credits, feature_key })
 * and lets the user redeem a code or create a buy-request without leaving
 * the page they were on.
 *
 * Strict rules:
 *  - No hardcoded credit cost — comes from `detail.required_credits`.
 *  - On successful redeem, balance is refreshed and the modal informs the
 *    caller (via `onResolved`) so it can retry the action.
 *  - Buy-request DOES NOT grant credits client-side.
 */
import React, { useEffect, useState } from 'react';
import { creditsApi, ApiError } from '../../lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Loader2, ShoppingCart, Sparkles, Coins, Gift } from 'lucide-react';
import { toast } from 'sonner';

const FEATURE_LABEL = {
  babyface_4k_generation: '4K Bebek Yüzü Üretimi',
  video_generation: '4D Video Simülasyonu',
  scan_enhancement: 'Tarama İyileştirme',
  ai_baby_face: 'AI Bebek Yüzü (Genetik Füzyon)',
};

export function InsufficientCreditsModal({
  open,
  detail,
  onClose,
  onResolved,
}) {
  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [redeemCode, setRedeemCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [redeemError, setRedeemError] = useState(null);
  const [buyingPackageId, setBuyingPackageId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoadingPackages(true);
    creditsApi
      .getPackages()
      .then((res) => setPackages(res.packages || []))
      .catch(() => setPackages([]))
      .finally(() => setLoadingPackages(false));
    setRedeemCode('');
    setRedeemError(null);
  }, [open]);

  const handleRedeem = async (e) => {
    e?.preventDefault?.();
    const code = (redeemCode || '').trim();
    if (!code) return;
    setRedeeming(true);
    setRedeemError(null);
    try {
      const result = await creditsApi.redeem(code);
      toast.success('Kod başarıyla kullanıldı.');
      // Hand the fresh account back to caller so it can retry generation.
      onResolved && onResolved(result?.account || null);
    } catch (err) {
      setRedeemError(mapRedeemError(err));
    } finally {
      setRedeeming(false);
    }
  };

  const handleBuyRequest = async (pkg) => {
    setBuyingPackageId(pkg.id);
    try {
      await creditsApi.buyRequest({
        package_id: pkg.id,
        contact_method: 'email',
      });
      toast.success(
        'Talebiniz alındı. Ödeme/onay sonrası krediniz tanımlanacak.',
        { duration: 5000 },
      );
      // Intentionally do NOT call onResolved — credits are not granted yet.
      onClose && onClose();
    } catch (err) {
      toast.error(err?.message || 'Talep oluşturulamadı.');
    } finally {
      setBuyingPackageId(null);
    }
  };

  const requiredCredits = detail?.required_credits ?? '?';
  const currentBalance = detail?.current_balance ?? 0;
  const featureKey = detail?.feature_key;
  const featureLabel = FEATURE_LABEL[featureKey] || 'Bu işlem';
  const messageTr =
    detail?.message_tr ||
    `${featureLabel} ${requiredCredits} kredi gerektiriyor. Mevcut bakiyeniz ${currentBalance}. Lütfen bir kredi paketi satın alın.`;

  return (
    <Dialog open={!!open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent
        className="max-w-lg"
        data-testid="insufficient-credits-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins className="h-5 w-5 text-amber-600" />
            Yetersiz Kredi
          </DialogTitle>
          <DialogDescription
            className="pt-1"
            data-testid="insufficient-credits-message"
          >
            {messageTr}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-sm flex items-center justify-between">
            <span className="text-slate-600">Gerekli</span>
            <span
              className="font-semibold tabular-nums"
              data-testid="insufficient-credits-required"
            >
              {requiredCredits} kredi
            </span>
          </div>
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-sm flex items-center justify-between">
            <span className="text-slate-600">Mevcut bakiye</span>
            <span
              className="font-semibold tabular-nums"
              data-testid="insufficient-credits-current"
            >
              {currentBalance} kredi
            </span>
          </div>

          <form onSubmit={handleRedeem} className="space-y-2">
            <label className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
              <Sparkles className="h-4 w-4 text-amber-600" />
              Hediye kodunuz var mı?
            </label>
            <div className="flex gap-2">
              <Input
                value={redeemCode}
                onChange={(e) => {
                  setRedeemCode(e.target.value.toUpperCase());
                  setRedeemError(null);
                }}
                placeholder="ÖR: NEOB-XXXX-XXXX"
                disabled={redeeming}
                data-testid="insufficient-credits-redeem-input"
              />
              <Button
                type="submit"
                disabled={redeeming || !redeemCode.trim()}
                data-testid="insufficient-credits-redeem-submit-btn"
              >
                {redeeming ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Kullan'
                )}
              </Button>
            </div>
            {redeemError && (
              <p
                className="text-xs text-red-600"
                data-testid="insufficient-credits-redeem-error"
              >
                {redeemError}
              </p>
            )}
          </form>

          {loadingPackages ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Paketler yükleniyor…
            </div>
          ) : packages.length === 0 ? (
            <div className="text-sm text-slate-500">
              Paket bulunamadı. Lütfen yöneticinizle iletişime geçin.
            </div>
          ) : (
            <div className="space-y-2">
              <div className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                <ShoppingCart className="h-4 w-4 text-indigo-600" />
                Kredi paketleri
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {packages.map((p) => (
                  <div
                    key={p.id}
                    className="rounded-lg border border-slate-200 p-3 flex flex-col gap-1"
                    data-testid={`insufficient-credits-package-${p.id}`}
                  >
                    <div className="flex items-baseline justify-between">
                      <div className="font-medium text-slate-900 text-sm">
                        {p.name}
                      </div>
                      <div className="text-xs text-slate-500">
                        {p.credit_amount} kredi
                      </div>
                    </div>
                    <div className="text-base font-semibold tabular-nums">
                      {p.price} {p.currency || 'TRY'}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={buyingPackageId === p.id}
                      onClick={() => handleBuyRequest(p)}
                      data-testid={`insufficient-credits-buy-btn-${p.id}`}
                    >
                      {buyingPackageId === p.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <span className="flex items-center gap-1.5">
                          <Gift className="h-3.5 w-3.5" />
                          Satın alma talebi
                        </span>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function mapRedeemError(err) {
  if (!(err instanceof ApiError)) {
    return err?.message || 'Kod kullanılırken bir hata oluştu.';
  }
  switch (err.status) {
    case 404:
      return 'Geçersiz kod.';
    case 410:
      return 'Bu kodun süresi dolmuş.';
    case 409:
      return 'Bu kod daha önce kullanılmış.';
    case 403:
      return 'Bu kod sizin hesap türünüz için geçerli değil.';
    default:
      return err.message || 'Kod kullanılırken bir hata oluştu.';
  }
}

/**
 * Convenience helper — checks if an error is HTTP 402 with INSUFFICIENT_CREDITS.
 * Used by callers to decide whether to open this modal.
 */
export function isInsufficientCreditsError(err) {
  return (
    err instanceof ApiError &&
    err.status === 402 &&
    (err.detail?.code === 'INSUFFICIENT_CREDITS' || err.detail?.feature_key)
  );
}
